import classNames from 'classnames';
import Decimal from 'decimal.js';
import { ComponentProps, forwardRef, useCallback, useMemo, useRef } from 'react';
import CurrencyFormat from 'react-currency-format';
import { MdOutlineClose } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { Divider, Whisper } from 'rsuite';
import { ExpandedRowPrice } from '../../../../../@types';
import { GerenciadorPrecos } from '../../../../../@types/GerenciadorPrecos';
import { RootState } from '../../../../../@types/RootState';
import { BaseCell, InputAddon, InputCurrency, InputGroup } from '../../../../../components';
import { AlertWithTimestamp } from '../../../../../components/AlertWithTimestamp';
import ExceptionSaveDatapoint from '../../../../../data/ExceptionSaveDatapoint';
import { IPAMaths } from '../../../../../lib';
import { selectorDatapoints, UPDATE_GERENCIADOR_DATAPOINT_BY_INDEX } from '../../../../../reducers/gerenciadorPrecos/datapoints';
import { REMOVE_GERENCIADOR_EXPANDED_ROW_OPTION } from '../../../../../reducers/gerenciadorPrecos/expandedRowKey';
import { selectorOfferDataView } from '../../../../../reducers/gerenciadorPrecos/offerDataView';
import { CompetitivenessPriceIndicator, MoneyPercentageIndicator, VariationBoxIndicator } from '../../../Negociacoes/pages/NegociacaoFornecedor/components';
import { SaveEditedOfferDatapoint, saveEditedOfferDatapoint } from '../../services';
import { hasHighlightedOfferPriceStatus } from '../../utils';
import { CompetitivenessLimitInfo } from '../CompetitivenessLimitInfo';
import { MarginLimitInfo } from '../MarginLimitInfo';
import { MarginInput } from '../MarginInput';
import PriceStatusIndicator from '../PriceStatusIndicator/PriceStatusIndicator';
import { CompetitorPriceOfferCell } from '../TableCells/CompetitorPriceOfferCell';
import { ExpandOfferCell } from '../TableCells/ExpandOfferCell';
import styles from './ExpandedRowOffer.module.scss';
import * as calc from './utils';
type RowData = GerenciadorPrecos.RowData;

export type ExpandedRowOfferProps = ComponentProps<'tr'> & {
  rowData: RowData;
  isAutoExpanded?: boolean;
  showPmcColumn?: boolean;
};

const ExpandedRowOffer = forwardRef<HTMLTableRowElement, ExpandedRowOfferProps>(
  ({ rowData, className, children, isAutoExpanded = false, showPmcColumn = true, ...props }, ref) => {
  const dispatch = useDispatch();
  const datapoints = useSelector(selectorDatapoints);
  const { autoExpandRows } = useSelector(selectorOfferDataView);
  const isDisabledEdit = useSelector((state: RootState) => {
    return state.reducerIPA.channels.offer.allowPriceEdit === false;
  });
  const datapointExhibitionType = useSelector((state: RootState) => state.datapointExhibitionType.datapointExhibitionType);

  const { productsToBePricedId } = rowData;

  const offer = rowData.offerDetails ?? null;

  const competitorsPrice = offer?.competitorsPrice || 0;

  const retailPrice = offer?.retailPrice || 0;
  const originalMargin = offer?.originalMargin || 0;
  const price = offer?.price || 0;
  const discountPercentage = offer?.discountPercentage || 0;
  const newCompetitivenessPrice = offer?.newCompetitivenessPrice || 0;
  const newMargin = offer?.newMargin ?? 0;
  const displayedNewMargin = newMargin || originalMargin;
  const marginFallbackRef = useRef<ExpandedRowPrice | null>(null);

  const competitivenessValue = useMemo(() => {
    return calc.calcOfferRetailCPI(retailPrice, competitorsPrice);
  }, [competitorsPrice, retailPrice]);
  const index = useMemo(() => datapoints.findIndex(item => item.productsToBePricedId === productsToBePricedId), [datapoints, productsToBePricedId]);

  const isCpiDisabled = useMemo(() => {
    if (!offer?.price || !offer?.competitorsPrice) return true;
    return false;
  }, [offer?.price, offer?.competitorsPrice]);

  const handleUpdateDatapointOfferByIndex = useCallback(
    (datapointIndex: number, updatedOffer: Partial<ExpandedRowPrice>) => {
      if (datapointIndex === -1) return;
      dispatch(
        UPDATE_GERENCIADOR_DATAPOINT_BY_INDEX({
          index: datapointIndex,
          data: { offerDetails: updatedOffer as ExpandedRowPrice },
        }),
      );
    },
    [dispatch],
  );

  const handleClose = useCallback(() => {
    if (isAutoExpanded || autoExpandRows) {
      return;
    }

    dispatch(REMOVE_GERENCIADOR_EXPANDED_ROW_OPTION('OFFER'));
    dispatch(REMOVE_GERENCIADOR_EXPANDED_ROW_OPTION('OFFERPRICE_COMPETITORS'));
  }, [autoExpandRows, dispatch, isAutoExpanded]);

  const handleUpdateCampo = useCallback(
    (name: string, value: number, rowData: RowData, index: number) => {
      if (!offer) return;

      let updatedOffer: Partial<ExpandedRowPrice> = { ...offer };

      // Use the offer's competitor price (not the regular row competitor price)
      const offerRowData = { ...rowData, competitorsPrice };

      const discountBasePrice = calc.getOfferDiscountBasePrice(rowData);

      if (name === 'price' && value <= 0) {
        updatedOffer = {
          ...updatedOffer,
          price: value,
          newCompetitivenessPrice: 0,
          newMargin: 0,
          discountPercentage: 100,
        };

        handleUpdateDatapointOfferByIndex(index, updatedOffer);

        return;
      }

      switch (name) {
        case 'price': {
          const newValues = calc.handleCalcOfferPrice(value, offerRowData);

          const cpi = competitorsPrice ? IPAMaths.novaCompetitividade(value, competitorsPrice) : 0;

          const discountPercentage = calc.calcOfferDiscountPercentage(rowData, value);

          updatedOffer = {
            ...updatedOffer,
            ...newValues,
            discountPercentage,
            newCompetitivenessPrice: cpi,
            priceStatus: 'EDITED',
          };
          break;
        }

        case 'CPI': {
          const newValues = calc.handleCalcOfferCPI(value, offerRowData);
          const discountPercentage = calc.calcOfferDiscountPercentage(rowData, newValues.price);

          updatedOffer = {
            ...updatedOffer,
            ...newValues,
            discountPercentage,
            priceStatus: 'EDITED',
          };
          break;
        }

        case 'margin': {
          const newValues = calc.handleCalcOfferMargin(value, offerRowData);

          if (newValues.price !== null) {
            (newValues as typeof newValues & { discountPercentage: number }).discountPercentage = calc.calcOfferDiscountPercentage(
              rowData,
              newValues.price,
            );
          }

          updatedOffer = {
            ...updatedOffer,
            ...newValues,
            priceStatus: 'EDITED',
          };
          break;
        }

        case 'discountPercentage': {
          const limitedDiscount = Math.max(0, Math.min(100, value));

          const newPrice = discountBasePrice ? IPAMaths.calcPrecoWithDiscount(discountBasePrice, limitedDiscount) : 0;

          const newValues = calc.handleCalcOfferPrice(newPrice, offerRowData);

          const newCPI = competitorsPrice ? IPAMaths.novaCompetitividade(newPrice, competitorsPrice) : 0;

          updatedOffer = {
            ...updatedOffer,
            ...newValues,
            discountPercentage: limitedDiscount,
            newCompetitivenessPrice: newCPI,
            priceStatus: 'EDITED',
          };

          if (new Decimal(newPrice || 0).lt(0.02)) {
            updatedOffer.newMargin = 0;
            updatedOffer.newCompetitivenessPrice = 0;
          }
          break;
        }
      }

      if (offer?.price !== updatedOffer.price || offer.discountPercentage !== updatedOffer.discountPercentage) {
        handleUpdateDatapointOfferByIndex(index, updatedOffer);
      }
    },
    [competitorsPrice, handleUpdateDatapointOfferByIndex, offer],
  );

  const handleBlur = useCallback(
    async (rowData: RowData) => {
      try {
        if (!offer) throw new Error('offer data not found');

        const model: SaveEditedOfferDatapoint.Data = {
          price: offer.price,
          productId: rowData.productId,
          storeId: rowData.storeId,
          productFamilyId: offer.productFamilyId,
          clusterId: offer.clusterId,
          datapointExhibitionType,
        };

        const res = await saveEditedOfferDatapoint(model);

        return res;
      } catch {
        const error = new ExceptionSaveDatapoint({
          productId: rowData.productId,
          storeId: rowData.storeId,
          storeName: rowData.storeName,
        });
        AlertWithTimestamp({ message: error.message });
      }
    },
    [datapointExhibitionType, offer],
  );

  if (!offer) return null;

  return (
    <tr {...props} ref={ref} className={styles.offerRow}>
      {/* blank column to fix layout */}
      <td style={{ width: '60px' }}>
        <ExpandOfferCell rowData={rowData} theme="blue">
          3
        </ExpandOfferCell>
      </td>
      <td style={{ minWidth: '200px' }}>
        <BaseCell>
          <BaseCell.MainContainer>Oferta</BaseCell.MainContainer>
        </BaseCell>
      </td>
      {/* store column blank to fix layout */}
      <td style={{ width: '80px' }}></td>
      {/* pmz column blank to fix layout */}
      <td style={{ width: '78px' }}></td>
      {/* margem objetiva column blank to fix layout */}
      <td style={{ width: '78px' }}></td>
      {/* pmc column blank to fix layout */}
      {showPmcColumn && <td style={{ width: '124px' }}></td>}
      <td style={{ width: '124px' }}>
        {retailPrice ? (
          <BaseCell>
            <BaseCell.MainContainer>
              <CurrencyFormat
                fixedDecimalScale
                decimalScale={2}
                value={retailPrice}
                displayType="text"
                prefix="R$ "
                decimalSeparator=","
                thousandSeparator="."
              />
            </BaseCell.MainContainer>
            <BaseCell.SubContainer>
              <MoneyPercentageIndicator value={originalMargin} tooltip="Margem do preço vigente" />
              <Divider vertical />
              <CompetitivenessPriceIndicator value={competitivenessValue} />
            </BaseCell.SubContainer>
          </BaseCell>
        ) : (
          <>--</>
        )}
      </td>
      <td style={{ width: '127px' }}>
        <CompetitorPriceOfferCell rowData={rowData} offer={offer} />
      </td>
      <td style={{ width: '132px' }}>
        <BaseCell>
          <BaseCell.MainContainer>
            <InputGroup>
              <InputAddon>R$</InputAddon>
              <InputCurrency
                value={price}
                name="price"
                decimalSeparator=","
                thousandSeparator="."
                precision="2"
                allowEmpty
                allowNegative={false}
                onChangeEvent={(_e, _m, value) => {
                  handleUpdateCampo('price', value, rowData, index);
                }}
                onBlur={(_e, _v, { hasChanges }) => {
                  if (hasChanges) handleBlur(rowData);
                }}
                skin={hasHighlightedOfferPriceStatus(rowData) ? 'blue' : 'gray'}
                disabled={isDisabledEdit}
              />
            </InputGroup>
          </BaseCell.MainContainer>
          <BaseCell.SubContainer>
            <VariationBoxIndicator value={IPAMaths.variation.price(price, retailPrice)} suffix="%" />
            <PriceStatusIndicator size="xxsmall" priceStatus={offer?.priceStatus} />
          </BaseCell.SubContainer>
        </BaseCell>
      </td>
      <td style={{ width: '116px' }}>
        <BaseCell>
          <BaseCell.MainContainer>
            <InputGroup>
              <InputCurrency
                name="CPI"
                decimalSeparator=","
                thousandSeparator="."
                precision="1"
                allowEmpty
                allowNegative={false}
                value={newCompetitivenessPrice || null}
                onChangeEvent={(_e, _m, value) => {
                  handleUpdateCampo('CPI', value, rowData, index);
                }}
                onBlur={(_e, _v, { hasChanges }) => {
                  if (hasChanges) handleBlur(rowData);
                }}
                disabled={isDisabledEdit || isCpiDisabled}
                skin={calc.calcOfferCpiSkin(offer)}
              />
              <Whisper
                trigger="click"
                placement="bottomEnd"
                speaker={
                  // @ts-ignore
                  <CompetitivenessLimitInfo content={offer} margin={newMargin} cpi={newCompetitivenessPrice} />
                }
              >
                <InputAddon>%</InputAddon>
              </Whisper>
            </InputGroup>
          </BaseCell.MainContainer>
          <BaseCell.SubContainer>
            <VariationBoxIndicator value={IPAMaths.variation.cpi(newCompetitivenessPrice, competitivenessValue)} />
          </BaseCell.SubContainer>
        </BaseCell>
      </td>
      <td style={{ width: '116px' }}>
        <BaseCell>
          <BaseCell.MainContainer>
            <InputGroup>
              <MarginInput
                name="margin"
                decimalSeparator=","
                thousandSeparator="."
                precision="1"
                allowEmpty
                allowNegative
                cost={rowData.cost}
                expensesPercent={rowData.expensesPercent}
                salesTaxPercentage={rowData.salesTaxPercentage}
                onFocus={() => {
                  marginFallbackRef.current = offer;
                }}
                onFallback={() => {
                  if (marginFallbackRef.current) {
                    handleUpdateDatapointOfferByIndex(index, marginFallbackRef.current);
                  }
                }}
                value={displayedNewMargin}
                onChangeEvent={(_e, _m, value) => {
                  handleUpdateCampo('margin', value, rowData, index);
                }}
                onBlur={(_e, _v, { hasChanges }) => {
                  if (hasChanges) handleBlur(rowData);
                }}
                disabled={isDisabledEdit}
                skin={calc.calcOfferMarginSkin(offer, displayedNewMargin)}
              />
              <Whisper
                trigger="click"
                placement="bottomEnd"
                speaker={
                  // @ts-ignore
                  <MarginLimitInfo content={offer} margin={newMargin} cpi={newCompetitivenessPrice} />
                }
              >
                <InputAddon>%</InputAddon>
              </Whisper>
            </InputGroup>
          </BaseCell.MainContainer>
          <BaseCell.SubContainer>
            {!!newMargin && !!originalMargin ? <VariationBoxIndicator value={IPAMaths.variation.margem(newMargin, originalMargin)} /> : <>--</>}
          </BaseCell.SubContainer>
        </BaseCell>
      </td>
      <td style={{ width: '140px' }}>
        <BaseCell>
          <BaseCell.MainContainer>
            <InputGroup>
              <InputCurrency
                name="discountPercentage"
                decimalSeparator=","
                thousandSeparator="."
                precision="1"
                allowEmpty
                allowNegative
                value={discountPercentage}
                onChangeEvent={(_e, _m, value) => {
                  handleUpdateCampo('discountPercentage', value, rowData, index);
                }}
                onBlur={(_e, _v, { hasChanges }) => {
                  if (hasChanges) handleBlur(rowData);
                }}
                disabled={isDisabledEdit}
              />
              <InputAddon className={styles.addonoffer}>% off</InputAddon>
            </InputGroup>
          </BaseCell.MainContainer>
          <BaseCell.SubContainer>
            <div className={classNames(styles.box, className)}>DESCONTO</div>
          </BaseCell.SubContainer>
        </BaseCell>
      </td>
      <td style={{ width: '26px' }}>
        <button className={styles.closeButton} onClick={handleClose} disabled={isAutoExpanded || autoExpandRows} aria-label="Fechar linha expandida">
          <MdOutlineClose size={16} />
        </button>
      </td>
    </tr>
  );
});

ExpandedRowOffer.displayName = 'ExpandedRowOffer';

export default ExpandedRowOffer;
